import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSportEventsByName, clearEventResult } from '../../../store/actions/sportEvents';
import { useEffectSkipFirst } from '../../../hooks/useEffectSkipFirst';
import AddTicketInput from './AddTicketInput/AddTicketInput';
import AddTicketSelect from './AddTicketSelect/AddTicketSelect';
import FullKeyboard from '../VirtualKeyboard/FullKeyboard';
import NumericKeyboard from '../VirtualKeyboard/NumericKeyboard';
import { addBetSlipBetLine } from '../../../store/actions/betSlip';
import { roundNumber } from '../../../utils/formatDecimal';
import EventsSearch from './EventsSearch/EventsSearch';
import { getTranslation } from '../../../utils/translation';
import { formatSpecifierName, formatTeamNames } from '../../../utils/render';
import { useHistory } from 'react-router-dom';
import { getEventsUrl } from '../../../utils/helpers';
import { setEventSearchProps, setShortcodeSearchProps } from '../../../store/actions/eventDetails';
import styles from './AddTicket.module.scss';
import { LANGUAGES } from '../../../constants/defaultValues';
import { generateRegularBetLine } from '../../../utils/betSlip';

const AddTicket = () => {
    const history = useHistory();

    const { locale } = useSelector(state => state.settings);

    const [selectedMarketId, setSelectedMarketId] = useState('');
    const [selectedOutcomeId, setSelectedOutcomeId] = useState('');
    const [disableAreas, setDisableAreas] = useState({
        shortcode: true,
        markets: true,
        outcomes: true,
    });

    const dispatch = useDispatch();
    const { eventsByName } = useSelector(state => state.sportEvents);
    const eventDetails = useSelector(state => state.eventDetails.event);
    const eventSearchProps = useSelector(state => state.eventDetails.eventSearch);
    const shortCodeSearchProps = useSelector(state => state.eventDetails.shortCode);

    const shortCodeInputRef = useRef();

    useEffectSkipFirst(() => {
        const { value } = eventSearchProps;

        if (value) {
            dispatch(getSportEventsByName(value));
        } else {
            cancelAddTicket();
        }
    }, [eventSearchProps.value]);

    useEffect(() => {
        if (eventDetails && Object.keys(eventDetails).length) {
            const teamNames = formatTeamNames(eventDetails.teams, locale);
            dispatch(
                setEventSearchProps({
                    value: `${eventDetails.programCode} ${teamNames.home} vs ${teamNames.away}`,
                })
            );
            setTimeout(() => {
                shortCodeInputRef.current?.focus();
            }, 200);
            if (eventDetails.markets?.length) {
                setDisableAreas({
                    ...disableAreas,
                    shortcode: false,
                    markets: false,
                });
            }
        } else {
            cancelAddTicket();
        }
    }, [eventDetails]);

    useEffect(() => {
        if (selectedMarketId) {
            const selectedMarket = getSelectedMarket(selectedMarketId);

            if (selectedMarket && selectedMarket.outcomes.length > 0) {
                setDisableAreas({
                    ...disableAreas,
                    outcomes: false,
                });
            } else {
                setDisableAreas({
                    ...disableAreas,
                    outcomes: true,
                });
            }
        }
    }, [selectedMarketId]);

    const onChangeInputHandler = (event, { input }) => {
        if (input === 'shortcode') {
            dispatch(setShortcodeSearchProps({ value: event.target.value }));
        }
    };

    const onChangeKeyboardValue = (value, { input }) => {
        if (input === 'shortcode') {
            dispatch(setShortcodeSearchProps({ value }));
        } else if (input === 'event') {
            dispatch(setEventSearchProps({ value }));
        }
    };

    const cancelAddTicket = () => {
        dispatch(clearEventResult());
        dispatch(setEventSearchProps({ isVisible: false, value: '' }));
        dispatch(setShortcodeSearchProps({ isVisible: false, value: '' }));
        setSelectedMarketId('');
        setSelectedOutcomeId('');
        setDisableAreas({
            shortcode: true,
            markets: true,
            outcomes: true,
        });
        if (eventDetails && Object.keys(eventDetails).length) {
            const redirectUrl = getEventsUrl({ event: eventDetails });
            history.push(redirectUrl);
        }
    };

    const getSelectedMarket = marketId => {
        return eventDetails?.markets?.find(m => m.id === marketId);
    };

    const addTicketSubmit = () => {
        if (!eventDetails || !selectedMarketId || !selectedOutcomeId) {
            return false;
        }

        addSelectedOutcome({
            eventDetails,
            marketId: selectedMarketId,
            outcomeId: selectedOutcomeId,
        });
    };

    const addSelectedOutcome = ({ eventDetails, marketId, outcomeId }) => {
        const selectedMarket = getSelectedMarket(marketId);
        const selectedOdd =
            selectedMarket && selectedMarket.outcomes.find(o => o.outcomeRefId === outcomeId);

        dispatch(
            addBetSlipBetLine(
                generateRegularBetLine({
                    event: eventDetails,
                    market: selectedMarket,
                    outcome: selectedOdd,
                })
            )
        );
    };

    const handleEnterPress = e => {
        if (e.key === 'Enter') {
            const addedToBetSlip = selectMarketAndOutcomeByShorcode(
                'shortcode',
                shortCodeSearchProps.value
            );
            dispatch(setShortcodeSearchProps({ value: '' }));

            if (addedToBetSlip) {
                const redirectUrl = getEventsUrl({ event: eventDetails });
                dispatch(clearEventResult());
                history.push(redirectUrl);
            }
        }
    };

    const selectMarketAndOutcomeByShorcode = (currentInput, inputValue) => {
        if (currentInput === 'shortcode' && inputValue !== '') {
            if (eventDetails?.markets) {
                let foundOutcomeData;

                eventDetails.markets.find(market => {
                    const foundOutcome = market.outcomes.find(
                        outcome => Number(outcome.shortCode) === Number(inputValue)
                    );
                    if (foundOutcome) {
                        foundOutcomeData = { outcome: foundOutcome, market };
                        return true;
                    }

                    return false;
                });

                if (foundOutcomeData) {
                    addSelectedOutcome({
                        eventDetails,
                        marketId: foundOutcomeData.market.id,
                        outcomeId: foundOutcomeData.outcome.outcomeRefId,
                    });
                }

                return !!foundOutcomeData;
            }
        }
    };

    const onCancelClick = () => {
        cancelAddTicket();
    };

    const selectedMarket = getSelectedMarket(selectedMarketId);
    const selectedOutcome =
        selectedMarket && selectedMarket.outcomes.find(o => o.outcomeRefId === selectedOutcomeId);
    const selectedOutcomeNames =
        selectedMarket && selectedOutcome
            ? selectedMarket.outcomeNames.find(o => o.id === selectedOutcome.outcomeRefId)?.names
            : {};

    return (
        <>
            {shortCodeSearchProps.isVisible && (
                <NumericKeyboard
                    inputValue={shortCodeSearchProps.value}
                    setInputValue={value => onChangeKeyboardValue(value, { input: 'shortcode' })}
                    close={() => dispatch(setShortcodeSearchProps({ isVisible: false }))}
                    style={{ top: '200px', left: '500px' }}
                />
            )}
            {eventSearchProps.isVisible && (
                <FullKeyboard
                    inputValue={eventSearchProps.value}
                    setInputValue={value => onChangeKeyboardValue(value, { input: 'event' })}
                    close={() => dispatch(setEventSearchProps({ isVisible: false }))}
                />
            )}
            <div className={styles.addTicket}>
                <EventsSearch events={eventsByName} searchValue={eventSearchProps.value} />
                <AddTicketInput
                    ref={shortCodeInputRef}
                    disabled={disableAreas.shortcode}
                    placeholder={getTranslation('input.placeholder.shortCode')}
                    inputValue={shortCodeSearchProps.value}
                    onChange={event => onChangeInputHandler(event, { input: 'shortcode' })}
                    onKeyPress={handleEnterPress}
                    setShowKeyboard={() => {
                        if (!disableAreas.shortcode) {
                            dispatch(
                                setShortcodeSearchProps({
                                    isVisible: !shortCodeSearchProps.isVisible,
                                })
                            );
                        }
                    }}
                    width={'240px'}
                    // style={{ flexBasis: '240px' }}
                />
                <AddTicketSelect
                    disabled={disableAreas.markets}
                    selectedItem={
                        selectedMarket
                            ? formatSpecifierName({
                                  name:
                                      selectedMarket.names?.[locale] ||
                                      selectedMarket.names?.[LANGUAGES.eng],
                                  specifiers: selectedMarket.specifiers,
                                  teams: formatTeamNames(eventDetails?.teams || {}, locale),
                                  tournamentName: eventDetails?.leagueName,
                                  playerNames: eventDetails?.playerNames,
                              })
                            : getTranslation('selectbox.selectMarket')
                    }
                    primary
                    marginRight='14'
                >
                    {eventDetails?.markets?.map(market => {
                        if (market.status === 0) {
                            return (
                                <div
                                    className={styles.selectedBoxItem}
                                    key={market.id}
                                    onClick={() => {
                                        setSelectedMarketId(market.id);
                                        setSelectedOutcomeId('');
                                    }}
                                >
                                    <span className={styles.itemValue}>
                                        {formatSpecifierName({
                                            name:
                                                market.names?.[locale] ||
                                                market.names?.[LANGUAGES.eng],
                                            specifiers: market.specifiers,
                                            teams: formatTeamNames(
                                                eventDetails?.teams || {},
                                                locale
                                            ),
                                            tournamentName: eventDetails?.leagueName,
                                            playerNames: eventDetails?.playerNames,
                                        })}
                                    </span>
                                    {selectedMarket && selectedMarket.id === market.id && (
                                        <span className={`${styles.checkIcon} icon-check`} />
                                    )}
                                </div>
                            );
                        }
                    })}
                </AddTicketSelect>
                <AddTicketSelect
                    selectedItem={
                        selectedOutcome
                            ? formatSpecifierName({
                                  name:
                                      eventDetails?.playerNames?.[selectedOutcome.outcomeRefId] ||
                                      selectedOutcomeNames[locale] ||
                                      selectedOutcomeNames[LANGUAGES.eng],
                                  specifiers: selectedMarket.specifiers,
                                  teams: formatTeamNames(eventDetails?.teams || {}, locale),
                                  tournamentName: eventDetails?.leagueName,
                                  playerNames: eventDetails?.playerNames,
                              })
                            : getTranslation('selectbox.selectOutcome')
                    }
                    disabled={disableAreas.outcomes}
                    primary
                >
                    {selectedMarket?.outcomes?.map((outcome, index) => {
                        const foundOutcomeNames =
                            selectedMarket.outcomeNames.find(o => o.id === outcome.outcomeRefId)
                                ?.names || {};

                        if (outcome.status === 0) {
                            return (
                                <div
                                    className={styles.selectedBoxItem}
                                    key={outcome.outcomeRefId}
                                    onClick={() => {
                                        setSelectedOutcomeId(outcome.outcomeRefId);
                                    }}
                                >
                                    <span className={styles.itemValue}>
                                        {formatSpecifierName({
                                            name:
                                                eventDetails?.playerNames?.[outcome.outcomeRefId] ||
                                                foundOutcomeNames[locale] ||
                                                foundOutcomeNames[LANGUAGES.eng],
                                            specifiers: selectedMarket.specifiers,
                                            teams: formatTeamNames(
                                                eventDetails?.teams || {},
                                                locale
                                            ),
                                            tournamentName: eventDetails?.leagueName,
                                            playerNames: eventDetails?.playerNames,
                                        })}{' '}
                                        - {roundNumber(outcome.odd, 2)}
                                    </span>
                                    {selectedOutcome &&
                                        selectedOutcome.outcomeRefId === outcome.outcomeRefId && (
                                            <span className={`${styles.checkIcon} icon-check`} />
                                        )}
                                </div>
                            );
                        } else {
                            return null;
                        }
                    })}
                </AddTicketSelect>
                <button
                    className={`${styles.button} ${styles.add}`}
                    disabled={!selectedOutcomeId}
                    onClick={() => addTicketSubmit()}
                >
                    {getTranslation('common.actions.addTicket')}
                </button>
                <button
                    className={`${styles.button} ${styles.cancel}`}
                    disabled={disableAreas.shortcode}
                    onClick={onCancelClick}
                >
                    {getTranslation('common.actions.cancel')}
                </button>
            </div>
        </>
    );
};

export default AddTicket;
